import { useCallback, useState } from 'react';
import { toPng } from 'html-to-image';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
if (!pdfMake.vfs && pdfFonts?.pdfMake?.vfs) {
  pdfMake.vfs = pdfFonts.pdfMake.vfs;
}

const PAGE_SIZES = {
  A4: { width: 595.28, height: 841.89 },
  A3: { width: 841.89, height: 1190.55 },
  Letter: { width: 612, height: 792 },
  Legal: { width: 612, height: 1008 },
  Tabloid: { width: 792, height: 1224 },
};

const PLACEHOLDER_IMAGE = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="#eee" width="100" height="100"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="#aaa">Image</text></svg>'
);

const isCrossOrigin = (url) => {
  if (!url || url.startsWith('data:') || url.startsWith('blob:')) return false;
  try {
    const imgUrl = new URL(url, window.location.origin);
    return imgUrl.origin !== window.location.origin;
  } catch {
    return false;
  }
};

const cleanClonedDom = (clonedDoc) => {
  const links = clonedDoc.querySelectorAll('link[rel="stylesheet"]');
  links.forEach((link) => {
    const href = link.getAttribute('href') || '';
    if (isCrossOrigin(href) || href.includes('fonts.googleapis.com') || href.includes('fonts.gstatic.com')) {
      link.remove();
    }
  });

  const images = clonedDoc.querySelectorAll('img');
  images.forEach((img) => {
    const src = img.getAttribute('src') || '';
    if (isCrossOrigin(src)) {
      img.setAttribute('src', PLACEHOLDER_IMAGE);
      img.setAttribute('crossorigin', 'anonymous');
    }
  });

  const bgElements = clonedDoc.querySelectorAll('[style*="background"]');
  bgElements.forEach((el) => {
    const style = el.getAttribute('style') || '';
    const urlMatch = style.match(/url\(["']?(https?:\/\/[^"')]+)["']?\)/);
    if (urlMatch && isCrossOrigin(urlMatch[1])) {
      el.style.backgroundImage = 'none';
      el.style.backgroundColor = '#e0e0e0';
    }
  });
};

export const usePdfExport = (options = {}) => {
  const [isExporting, setIsExporting] = useState(false);

  const {
    pageSize = 'A4',
    orientation = 'auto',
    margin = 40,
    pixelRatio = 2
  } = options;

  const exportToPdf = useCallback(async (elementRef, filename = 'export.pdf') => {
    if (!elementRef?.current) return;

    setIsExporting(true);
    try {
      const element = elementRef.current;

      const dataUrl = await toPng(element, {
        pixelRatio,
        cacheBust: true,
        skipFonts: true,
        imagePlaceholder: PLACEHOLDER_IMAGE,
        includeQueryParams: true,
        onclone: (clonedDocument) => {
          cleanClonedDom(clonedDocument);
        },
        filter: (node) => {
          const exclusionClasses = ['no-pdf', 'export-button'];
          return !exclusionClasses.some(cls => node.classList?.contains?.(cls));
        },
      });

      const { width, height } = element.getBoundingClientRect();

      // Calculate orientation
      const isLandscape = orientation === 'auto' ? width > height : orientation === 'landscape';
      const dimensions = PAGE_SIZES[pageSize] || PAGE_SIZES.A4;

      const pWidth = isLandscape ? dimensions.height : dimensions.width;
      const pHeight = isLandscape ? dimensions.width : dimensions.height;

      // Calculate scaling to fit the page while maintaining aspect ratio
      const printableWidth = pWidth - (margin * 2);
      const printableHeight = pHeight - (margin * 2);
      const scale = Math.min(printableWidth / width, printableHeight / height);

      const docDefinition = {
        pageSize,
        pageOrientation: isLandscape ? 'landscape' : 'portrait',
        pageMargins: [margin, margin, margin, margin],
        content: [
          {
            image: dataUrl,
            width: width * scale,
            alignment: 'center'
          }
        ],
        // Set metadata
        info: { title: filename, creator: 'Vibe-Agent PDF Export' }
      };

      pdfMake.createPdf(docDefinition).download(filename);
    } catch (error) {
      console.error('PDF Export Error:', error);
      throw error;
    } finally {
      setIsExporting(false);
    }
  }, [pageSize, orientation, margin, pixelRatio]); // Depend on primitives, not the object

  return { exportToPdf, isExporting };
};
